import { Link } from "wouter";
import { Shield, Tag, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BOOK_DIRECT_SAVINGS,
  STRIPE_SECURE_PAYMENT,
} from "@shared/property-facts";

interface WhyBookDirectProps {
  className?: string;
  variant?: "default" | "compact";
}

const items = [
  {
    icon: Tag,
    title: "Lower total cost",
    body: BOOK_DIRECT_SAVINGS,
  },
  {
    icon: Shield,
    title: "Secure Stripe checkout",
    body: STRIPE_SECURE_PAYMENT,
  },
  {
    icon: FileText,
    title: "Clear policies",
    body: "Cancellation and refund rules are published before you pay.",
    href: "/policies",
    linkLabel: "Full policy",
  },
] as const;

/**
 * Compact trust block for Stay, Booking, and Checkout.
 * Qualitative only: no dollar or percent savings claims.
 */
export function WhyBookDirect({ className, variant = "default" }: WhyBookDirectProps) {
  const compact = variant === "compact";

  return (
    <div
      className={cn(
        "border border-sand-deep bg-shell",
        compact ? "rounded-xl p-4" : "rounded-2xl p-6 md:p-8",
        className,
      )}
      data-testid="why-book-direct"
    >
      <p className="text-[11px] md:text-xs font-sans font-semibold uppercase tracking-[0.3em] text-driftwood mb-2">
        Why book direct
      </p>
      <h2
        className={cn(
          "font-serif font-normal text-forest tracking-luxury-tight",
          compact ? "text-luxury-lg mb-4" : "text-luxury-2xl mb-6",
        )}
      >
        A real stay, without platform fees
      </h2>
      <ul className={cn("space-y-4", compact && "space-y-3")}>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.title} className="flex items-start gap-3">
              <span
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-sand-deep bg-sand"
                aria-hidden="true"
              >
                <Icon className="h-4 w-4 text-palm" />
              </span>
              <div className="min-w-0">
                <p className="font-sans font-semibold text-ink text-luxury-sm">{item.title}</p>
                <p className="font-sans text-luxury-sm text-ink-soft leading-relaxed mt-0.5">
                  {item.body}
                  {"href" in item && item.href ? (
                    <>
                      {" "}
                      <Link
                        href={item.href}
                        className="inline-flex min-h-[44px] items-center text-forest font-semibold underline decoration-sand-deep underline-offset-4 hover:text-ink"
                      >
                        {item.linkLabel}
                      </Link>
                    </>
                  ) : null}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
